import React, {useEffect, useMemo, useState} from 'react';
import {getApiService, useApi} from '@/services/api';
import {getUserFriendlyErrorMessage} from '@/utils/errorHandling';
import {
  Button,
  ButtonVariant, DeleteConfirmationPopup,
  HeadingSize,
  Label,
  LabelType,
  Search, Table, TableColumn,
  useToast
} from '@fs/uikit-react';
import './Settlements.css';

interface Settlement {
  id: string;
  name: string;
  type: string;
  region: string;
  district: string | null;
}

const SettlementsPage = () => {
  const [searchValue, setSearchValue] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [settlementToDelete, setSettlementToDelete] = useState<Settlement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSettlement, setCurrentSettlement] = useState<Settlement | null>(null);
  const [settlementName, setSettlementName] = useState('');
  const [settlementType, setSettlementType] = useState('');
  const [settlementRegion, setSettlementRegion] = useState('');
  const [settlementDistrict, setSettlementDistrict] = useState('');

  const toast = useToast();

  // Functions to handle delete confirmation popup
  const openDeleteModal = (settlement: Settlement, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent opening edit modal
    }
    setSettlementToDelete(settlement);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSettlementToDelete(null);
  };

  // Functions to handle create/edit modal
  const openCreateModal = () => {
    setCurrentSettlement(null);
    setSettlementName('');
    setSettlementType('');
    setSettlementRegion('');
    setSettlementDistrict('');
    setIsModalOpen(true);
  };

  const openEditModal = (settlement: Settlement) => {
    setCurrentSettlement(settlement);
    setSettlementName(settlement.name);
    setSettlementType(settlement.type);
    setSettlementRegion(settlement.region);
    setSettlementDistrict(settlement.district || '');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentSettlement(null);
    setSettlementName('');
    setSettlementType('');
    setSettlementRegion('');
    setSettlementDistrict('');
  };

  // Function to delete settlement
  const deleteSettlement = async () => {
    if (!settlementToDelete) return;

    try {
      await apiService.client.delete(`/ato/${settlementToDelete.id}`);
      refreshSettlements(); // Refresh the settlement list
      closeDeleteModal();
      toast.showSuccessToast('Населений пункт видалено успішно');
    } catch (error) {
      console.error('Error deleting settlement:', error);
      toast.showErrorToast(
        'Помилка видалення населеного пункту',
        getUserFriendlyErrorMessage(error)
      );
    }
  };

  // Function to save settlement (create or update)
  const saveSettlement = async () => {
    if (!settlementName.trim()) {
      toast.showErrorToast('Помилка', 'Назва населеного пункту не може бути порожньою');
      return;
    }

    if (!settlementType.trim()) {
      toast.showErrorToast('Помилка', 'Тип населеного пункту не може бути порожнім');
      return;
    }

    if (!settlementRegion.trim()) {
      toast.showErrorToast('Помилка', 'Регіон не може бути порожнім');
      return;
    }

    try {
      if (currentSettlement) {
        // Update existing settlement
        await apiService.client.put(`/ato/${currentSettlement.id}`, {
          name: settlementName,
          type: settlementType,
          region: settlementRegion,
          district: settlementDistrict || null
        });
        toast.showSuccessToast('Населений пункт оновлено успішно');
      } else {
        // Create new settlement
        await apiService.client.post('/ato', {
          name: settlementName,
          type: settlementType,
          region: settlementRegion,
          district: settlementDistrict || null,
          isExisted: true
        });
        toast.showSuccessToast('Населений пункт створено успішно');
      }
      refreshSettlements(); // Refresh the settlement list
      closeModal();
    } catch (error) {
      console.error('Error saving settlement:', error);
      toast.showErrorToast(
        'Помилка збереження населеного пункту',
        getUserFriendlyErrorMessage(error)
      );
    }
  };

  // Get API service inside the component to ensure it's initialized
  // Memoize the API service to prevent it from changing on each render
  const apiService = useMemo(() => getApiService(), []);
  // Memoize the API call function to prevent it from changing on each render
  const apiCallFunction = useMemo(() =>
      (client: { get: (arg0: string) => any; }) => client.get('/ato/settlements'),
    []
  );

  const {
    data: settlements,
    loading,
    error,
    execute: refreshSettlements,
  } = useApi<Settlement[]>(
    apiService.client,
    apiCallFunction,
    apiService.loadingStateManager,
    'settlements-list'
  );

  useEffect(() => {
    if (error) {
      console.error('Error fetching settlements:', error);
    }
  }, [error]);

  // Filter settlements based on search value
  const filteredSettlements = settlements?.filter(settlement => {
    if (searchValue === '') return true;
    return (
      settlement.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      settlement.region.toLowerCase().includes(searchValue.toLowerCase()) ||
      (settlement.district && settlement.district.toLowerCase().includes(searchValue.toLowerCase()))
    );
  }) || [];

  // Define table columns
  const columns: TableColumn[] = [
    {
      key: 'name',
      title: 'Назва',
      width: '25%',
      align: 'left'
    },
    {
      key: 'type',
      title: 'Тип',
      width: '15%',
      align: 'left'
    },
    {
      key: 'region',
      title: 'Регіон',
      width: '30%',
      align: 'left'
    },
    {
      key: 'district',
      title: 'Район',
      width: '30%',
      align: 'left'
    }
  ];

  // Transform data for the table
  const tableData = filteredSettlements.map(settlement => {
    return {
      id: settlement.id,
      name: (
        <Label
          text={settlement.name}
          type={LabelType.PARAGRAPH}
        />
      ),
      type: (
        <Label
          text={settlement.type}
          type={LabelType.PARAGRAPH}
        />
      ),
      region: (
        <Label
          text={settlement.region}
          type={LabelType.PARAGRAPH}
        />
      ),
      district: (
        <Label
          text={settlement.district || '-'}
          type={LabelType.PARAGRAPH}
        />
      ),
      // Store the original settlement object for use in click handler
      _settlement: settlement
    };
  });

  return (
    <div className={"container_page"}>
      <div className="title">
        <Label
          text={'Населені пункти'}
          type={LabelType.HEADING}
          headingSize={HeadingSize.L}
        />
        <div className="button-container">
          <Button onClick={openCreateModal} title="Створити населений пункт"/>
        </div>
      </div>

      <div>
        <div className="search-button-container">
          <div className="search-container">
            <Search
              placeholder="Пошук населених пунктів"
              value={searchValue}
              onChange={setSearchValue}
            />
          </div>
        </div>

        <Table
          data={tableData}
          columns={columns}
          onClick={(record) => { openEditModal(record._settlement); }}
          className="settlements-table"
          enablePagination={tableData.length > 10}
          paginationConfig={{
            pageSize: 10,
            pageSizeOptions: [10, 20, 50],
            showSizeChanger: true
          }}
          rowActions={[
            {
              type: 'delete',
              onClick: (record) => { openDeleteModal(record._settlement); }
            }
          ]}
        />

        {loading && <div className="loading-indicator">Завантаження...</div>}

        {!loading && error && (
          <div className="error-message">
            Помилка завантаження населених
            пунктів: {getUserFriendlyErrorMessage(error)}
          </div>
        )}

        {!loading && !error && tableData.length === 0 && (
          <div className="empty-message">
            Немає доступних населених пунктів
          </div>
        )}
      </div>

      {isDeleteModalOpen &&
          <DeleteConfirmationPopup
              onClose={closeDeleteModal}
              name={'населений пункт'}
              confirmButtonText="Видалити"
              onConfirmButtonClick={deleteSettlement}
          />
      }

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{currentSettlement ? 'Редагувати населений пункт' : 'Створити населений пункт'}</h2>
              <button className="close-button" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="settlementName">Назва</label>
                <input
                  id="settlementName"
                  type="text"
                  value={settlementName}
                  onChange={(e) => { setSettlementName(e.target.value); }}
                  placeholder="Введіть назву населеного пункту"
                />
              </div>
              <div className="form-group">
                <label htmlFor="settlementType">Тип</label>
                <input
                  id="settlementType"
                  type="text"
                  value={settlementType}
                  onChange={(e) => { setSettlementType(e.target.value); }}
                  placeholder="Введіть тип населеного пункту"
                />
              </div>
              <div className="form-group">
                <label htmlFor="settlementRegion">Регіон</label>
                <input
                  id="settlementRegion"
                  type="text"
                  value={settlementRegion}
                  onChange={(e) => { setSettlementRegion(e.target.value); }}
                  placeholder="Введіть регіон"
                />
              </div>
              <div className="form-group">
                <label htmlFor="settlementDistrict">Район</label>
                <input
                  id="settlementDistrict"
                  type="text"
                  value={settlementDistrict}
                  onChange={(e) => { setSettlementDistrict(e.target.value); }}
                  placeholder="Введіть район (необов'язково)"
                />
              </div>
            </div>
            <div className="modal-footer">
              <Button
                variant={ButtonVariant.SECONDARY}
                onClick={closeModal}
                title="Скасувати"
              />
              <Button onClick={saveSettlement} title="Зберегти"/>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettlementsPage;
